import {
  PublicClientApplication,
  LogLevel,
  type Configuration,
} from '@azure/msal-browser';

/**
 * Delegated, public-client MSAL config (§2). No secret in the browser.
 * PKCE is used automatically by msal-browser for the auth-code flow.
 *
 * Token cache lives in sessionStorage — NOT localStorage (§3.3).
 */
const clientId = (import.meta.env.VITE_AAD_CLIENT_ID as string | undefined) ?? '';
const authority =
  (import.meta.env.VITE_AAD_AUTHORITY as string | undefined) ||
  'https://login.microsoftonline.com/common';
/**
 * Popups (loginPopup / acquireTokenPopup) and the silent-refresh iframe land on
 * this URI. It points at a minimal blank page (public/blank.html) that does NOT
 * boot MSAL — critical for popup flow: if the popup instead loaded the full SPA,
 * the app's handleRedirectPromise() would consume the auth response before the
 * opener window could read it, so the popup would sit on the app page and never
 * close. A dedicated blank page is Microsoft's recommended fix.
 *
 * Register this exact URI (…/blank.html) as an SPA redirect URI in the app
 * registration. Override via VITE_AAD_REDIRECT_URI only if you host blank.html
 * elsewhere.
 */
const redirectUri =
  (import.meta.env.VITE_AAD_REDIRECT_URI as string | undefined) ||
  `${window.location.origin}/blank.html`;

const PLACEHOLDER_CLIENT_ID = '00000000-0000-0000-0000-000000000000';

/**
 * True only when a real app-registration client ID has been supplied. When false
 * the UI shows a setup screen instead of firing an auth request with an undefined
 * client ID (which surfaces as the confusing AADSTS700016 "application 'undefined'
 * was not found" error).
 */
export const isAuthConfigured = clientId.length > 0 && !clientId.startsWith('00000000');

if (!isAuthConfigured) {
  // Loud, early signal in the console; the UI also blocks sign-in (see ConnectGate).
  console.warn(
    '[auth] VITE_AAD_CLIENT_ID is not set. Copy .env.example to .env, set your ' +
      'app-registration Application (client) ID, and restart the dev server (or set it ' +
      'in your static host’s build environment).',
  );
}

export const msalConfig: Configuration = {
  auth: {
    // A non-empty value is required for the MSAL constructor. When unconfigured we
    // pass a well-formed placeholder so construction succeeds; the UI still blocks
    // sign-in via isAuthConfigured, so no request is ever sent with this value.
    clientId: clientId || PLACEHOLDER_CLIENT_ID,
    authority,
    redirectUri,
    // Logout returns to the app root, not the blank page.
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: false,
  },
  cache: {
    // §3.3 token hygiene: sessionStorage, never localStorage.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      // No PII, no remote sink — logs stay in the browser console only.
      piiLoggingEnabled: false,
      logLevel: LogLevel.Warning,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error(message);
        else if (level === LogLevel.Warning) console.warn(message);
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);
